import React from 'react';
import { Box, Typography, Paper, Divider, useTheme, CircularProgress, IconButton, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import ReactMarkdown from 'react-markdown';
import ShareButton from './ShareButton';
import ShareModal from './ShareModal';
import PineScriptButton from './PineScriptButton';

export interface AIResponse {
  id: number;
  content: string;
  image?: string; // Optional image for sharing
}

interface AIResponsePanelProps {
  responses?: AIResponse[];
  loading?: boolean;
}

const getStoredVoice = () => localStorage.getItem('ttsVoice') || 'alloy';

const getJwtFromCookie = () => {
  const match = document.cookie.match(/(?:^|; )jwt=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const AIResponsePanel: React.FC<AIResponsePanelProps> = ({ responses = [], loading = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [playingId, setPlayingId] = React.useState<number | null>(null);
  const [isPaused, setIsPaused] = React.useState(false);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  // Share modal state
  const [shareOpen, setShareOpen] = React.useState(false);
  const [shareText, setShareText] = React.useState('');
  const [shareImage, setShareImage] = React.useState<string | undefined>(undefined); // Placeholder for image
  const [shareResponseId, setShareResponseId] = React.useState<number | null>(null);

  // Auto-scroll to bottom when new responses are added or loading state changes
  React.useEffect(() => {
    if (scrollContainerRef.current && (responses.length > 0 || loading)) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [responses.length, loading]);

  const playTTS = async (text: string, id: number) => {
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setIsPaused(false);
    setIsPlaying(false);
    setPlayingId(id);
    try {
      const voice = getStoredVoice();
      const jwt = getJwtFromCookie();
      const res = await fetch('/api/v1/ai/tts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify({ text, voice }),
      });
      if (!res.ok) throw new Error('Failed to fetch audio');
      const audioBlob = await res.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.onplay = () => {
        setIsPlaying(true);
      };
      audio.onended = () => {
        setPlayingId(null);
        setIsPaused(false);
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };
      audio.play();
    } catch (err) {
      setPlayingId(null);
      setIsPaused(false);
      setIsPlaying(false);
      alert('Could not play audio: ' + (err as Error).message);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPaused(true);
      setIsPlaying(false);
    }
  };

  const resumeAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPaused(false);
      // isPlaying will be set by onplay
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setPlayingId(null);
    setIsPaused(false);
    setIsPlaying(false);
  };

  const handleOpenShare = (resp: AIResponse) => {
    setShareText(resp.content);
    setShareImage(resp.image); // Use the image if available
    setShareResponseId(resp.id);
    setShareOpen(true);
  };
  const handleCloseShare = () => {
    setShareOpen(false);
    setShareResponseId(null);
  };
  const handleShareTextChange = (text: string) => setShareText(text);
  const handleShare = (platform: string) => {
    // TODO: Implement sharing logic for each platform
    // For now, just close the modal and show a placeholder alert
    setShareOpen(false);
    setShareResponseId(null);
    alert(`Shared to ${platform}!`);
  };

  return (
    <Paper elevation={4} sx={{ maxWidth: 800, width: '100%', mx: 'auto', mt: 4, mb: 4, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'transparent', borderRadius: 3, p: 1, backdropFilter: (theme) => theme.palette.mode === 'dark' ? 'blur(20px)' : undefined, WebkitBackdropFilter: (theme) => theme.palette.mode === 'dark' ? 'blur(20px)' : undefined, border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : undefined }} role="region" aria-label="AI Response History">
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          p: 1.2, 
          borderBottom: 0, 
          background: 'transparent',
          border: '2px solid rgba(25,118,210,0.04)',
          borderTopLeftRadius: 22, 
          borderTopRightRadius: 22, 
          borderBottomLeftRadius: 18,
          borderBottomRightRadius: 18,
          mb: 1,
        }}
      >
        <AutoAwesomeIcon sx={{ mr: 1, fontSize: 20, color: 'primary.light', filter: 'none' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'primary.main', letterSpacing: 0.2, fontSize: 16, textShadow: 'none' }}>
          AI Response
        </Typography>
      </Box>
      <Box sx={{ 
        maxHeight: 400, 
        overflowY: 'auto', 
        p: 1,
        scrollBehavior: 'smooth',
      }} ref={scrollContainerRef}>
        {responses.length === 0 && !loading ? (
          <Typography variant="body2" color="text.secondary">No AI responses yet.</Typography>
        ) : (
          <>
            {responses.map((resp, index) => (
              <Box key={resp.id} sx={{ 
                mb: index < responses.length - 1 ? 2 : 0,
                position: 'relative', // For ShareButton positioning
                ...(index === responses.length - 1 && {
                  border: isDark ? '2px solid rgba(255,255,255,0.1)' : '2px solid',
                  borderColor: isDark ? undefined : 'primary.main',
                  borderRadius: 2,
                  p: 1,
                  background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(25, 118, 210, 0.05)',
                  backdropFilter: isDark ? 'blur(20px)' : undefined,
                  WebkitBackdropFilter: isDark ? 'blur(20px)' : undefined,
                })
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 600, fontSize: 14 }}>
                    Response #{index + 1}
                    {index === responses.length - 1 && (
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: 'success.main', fontWeight: 'bold' }}>
                        (Latest)
                      </Typography>
                    )}
                  </Typography>
                  <Tooltip title="Read aloud" placement="top">
                    <span>
                      <IconButton
                        aria-label="Read aloud"
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={() => playTTS(resp.content, resp.id)}
                        disabled={playingId === resp.id}
                      >
                        {playingId === resp.id && !isPlaying && !isPaused ? <CircularProgress size={18} /> : <VolumeUpIcon fontSize="small" />}
                      </IconButton>
                      {playingId === resp.id && (isPlaying || isPaused) && (
                        <>
                          <IconButton
                            aria-label={isPaused ? 'Resume' : 'Pause'}
                            size="small"
                            sx={{ ml: 1 }}
                            onClick={isPaused ? resumeAudio : pauseAudio}
                          >
                            {isPaused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                          </IconButton>
                          <IconButton
                            aria-label="Stop"
                            size="small"
                            sx={{ ml: 1 }}
                            onClick={stopAudio}
                          >
                            <StopIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </span>
                  </Tooltip>
                </Box>
                <Box sx={{ 
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.18)' : 'transparent',
                  color: isDark ? 'common.white' : 'text.primary',
                  p: 0.5, 
                  borderRadius: 2, 
                  border: '1px solid',
                  borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'grey.300',
                  fontSize: 13,
                  lineHeight: 1.5,
                  backdropFilter: (theme) => theme.palette.mode === 'dark' ? 'blur(20px)' : undefined,
                  WebkitBackdropFilter: (theme) => theme.palette.mode === 'dark' ? 'blur(20px)' : undefined,
                  '&:focus-within': { outline: '2px solid #4facfe' }
                }}>
                  <ReactMarkdown>{resp.content}</ReactMarkdown>
                </Box>
                {/* Pine Script Button for Trading Analysis */}
                <PineScriptButton 
                  aiResponseText={resp.content}
                  assetName="Trading Asset"
                />
                {/* Share Button in lower right */}
                <ShareButton onClick={() => handleOpenShare(resp)} />
                {index < responses.length - 1 && (
                  <Divider sx={{ mt: 1, borderColor: isDark ? 'grey.700' : 'grey.300' }} />
                )}
              </Box>
            ))}
            {loading && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mt: 2,
                p: 2,
                bgcolor: isDark ? 'rgba(0,0,0,0.18)' : 'rgba(25, 118, 210, 0.05)',
                borderRadius: 2,
                border: '1px solid',
                borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'primary.main',
                backdropFilter: isDark ? 'blur(20px)' : undefined,
                WebkitBackdropFilter: isDark ? 'blur(20px)' : undefined,
              }}>
                <CircularProgress size={18} color="primary" />
                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                  AI is thinking...
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
      {/* Share Modal (outside the map, but inside Paper) */}
      <ShareModal
        open={shareOpen}
        onClose={handleCloseShare}
        responseText={shareText}
        responseImage={shareImage}
        onTextChange={handleShareTextChange}
        onShare={handleShare}
      />
    </Paper>
  );
};

export default AIResponsePanel; 